/* eslint-disable unused-imports/no-unused-vars */
"use client";

import { RequestType, SellCarRequestCreateDTO, SellCarRequestResponseDTO } from "@alba-cars/common-modules";
import { useState } from "react";
import { toast } from "sonner";

import CarDetailsStep from "./CarDetailsStep";
import PersonalInformationStep from "./PersonalInformationStep";
import SecondStep from "./SecondStep";
import SuccessStep from "./SuccessStep";
import MultistepForm, { StepData } from "@/components/common/forms/MultiStepForm";
import { getOrCreateLead } from "@/lib/api/lead/get-or-create-lead";
import { createSellCarRequest } from "@/lib/api/sell-car-request/create-sell-car-request";
import { notifyWebhookAboutUpdate } from "@/lib/api/sell-car-request/notify-update-webhook";
import {
  FirstStepSchema,
  SecondStepSchema,
  SellCarFormSchema,
  SellCarFormSchemaType,
  ThirdStepSchema,
} from "@/schemas/validation/sellCarFormSchema";

interface ClientFormWrapperProps {
  title: string;
  description: string;
}

const stepsData: StepData[] = [
  {
    component: PersonalInformationStep,
    onStepSubmit: async (formData: FormData, previousFormData: FormData) => {},
    step: { label: "Personal details", index: 0 },
  },
  {
    component: CarDetailsStep,
    onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
      const parsedData = FirstStepSchema.parse(previousFormData);

      const response = await getOrCreateLead({
        ...parsedData,
        source: "sell-car",
        status: "ABANDONED_FORM",
        webhookData: { sellCarDetails: { ...formData }, vehicleImageUrls: [], source: "sell-car-form" },
      });

      if (!response.isNew) {
        //* The lead already exists, so we need to manually trigger the webhook
        await notifyWebhookAboutUpdate({
          lead: { ...parsedData },
          sellCarDetails: { ...formData },
          vehicleImageUrls: [],
          message: "Client updated with car details",
          source: "sell-car-form",
        });
      }
    },
    step: { label: "Car details", index: 1 },
  },
  {
    component: SecondStep,
    onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
      const { name, phone, ...rest } = previousFormData as any;
      const data = formData as any;
      const imageUrls = (data["images"] ?? []) as unknown as any[];

      const updatePayload = {
        lead: { name, phone },
        sellCarDetails: { ...rest },
        vehicleImageUrls: imageUrls?.map((image) => image.url),
        message: "Client updated with car images",
        source: "sell-car-form",
      };

      await notifyWebhookAboutUpdate(updatePayload);
    },
    step: { label: "Images", index: 2 },
    submitStep: true,
  },
  {
    component: SuccessStep,
    onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
      console.log("Step 3 Submitted", formData);
    },
    isSuccessStep: true,
  },
];

const stepTitles = ["Step 1: User Details", "Step 2: Car Details", "Step 3: Submit"];

const schemas = {
  0: FirstStepSchema,
  1: SecondStepSchema,
  2: ThirdStepSchema,
};

export default function ClientFormWrapper({ title, description }: ClientFormWrapperProps) {
  const [apiResponse, setApiResponse] = useState<SellCarRequestResponseDTO | null>(null);

  const handleFinalSubmit = async (data: SellCarFormSchemaType) => {
    try {
      const parsedData = SellCarFormSchema.parse(data);

      let dto = new SellCarRequestCreateDTO() as Omit<SellCarRequestCreateDTO, "validate" | "toPlain" | "emirates">;
            dto = {
        ...parsedData,
        requestType: RequestType.SELL,
        description: parsedData.description ?? "",
      };

      if (parsedData.modelVersion) {
        dto.modelVersion = parsedData.modelVersion;
        dto.modelVersionName = parsedData.modelVersionName ?? "";
      }

      const response = await createSellCarRequest(dto);

      setApiResponse(response);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit your sell request.");
    }
  };

  return (
    <MultistepForm<SellCarFormSchemaType>
      stepsData={stepsData.map((step) => ({
        ...step,
        component:
          step.component === SuccessStep
            ? (props: any) => <SuccessStep {...props} apiResponse={apiResponse} />
            : step.component,
      }))}
      onSubmit={handleFinalSubmit}
      stepTitles={stepTitles}
      title={title}
      description={description}
      schemas={schemas}
    />
  );
}
